import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const GATEWAY_URL = "https://connector-gateway.lovable.dev/brevo";

function authHeaders() {
  const LOVABLE_API_KEY = process.env.LOVABLE_API_KEY;
  const BREVO_API_KEY = process.env.BREVO_API_KEY;
  if (!LOVABLE_API_KEY) throw new Error("LOVABLE_API_KEY ni nastavljen");
  if (!BREVO_API_KEY) throw new Error("Brevo ni povezan (BREVO_API_KEY manjka)");
  return {
    Authorization: `Bearer ${LOVABLE_API_KEY}`,
    "X-Connection-Api-Key": BREVO_API_KEY,
    "Content-Type": "application/json",
  };
}

export type BrevoList = {
  id: number;
  name: string;
  totalSubscribers: number;
  uniqueSubscribers: number;
};

export const listBrevoLists = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<{ lists: BrevoList[] }> => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);

    const all: BrevoList[] = [];
    let offset = 0;
    const limit = 50;
    for (let page = 0; page < 20; page++) {
      const res = await fetch(
        `${GATEWAY_URL}/contacts/lists?limit=${limit}&offset=${offset}`,
        { headers: authHeaders() },
      );
      const body = await res.json();
      if (!res.ok) throw new Error(`Brevo [${res.status}]: ${JSON.stringify(body)}`);
      const lists: any[] = body.lists ?? [];
      for (const l of lists) {
        all.push({
          id: l.id,
          name: l.name,
          totalSubscribers: l.totalSubscribers ?? 0,
          uniqueSubscribers: l.uniqueSubscribers ?? 0,
        });
      }
      if (lists.length < limit) break;
      offset += limit;
    }
    all.sort((a, b) => a.name.localeCompare(b.name, "sl"));
    return { lists: all };
  });

export const importBrevoList = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        listId: z.number().int().positive(),
        label: z.string().trim().min(1).max(120),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const headers = authHeaders();
    const emails = new Set<string>();
    let offset = 0;
    const limit = 500;
    for (let page = 0; page < 200; page++) {
      const res = await fetch(
        `${GATEWAY_URL}/contacts/lists/${data.listId}/contacts?limit=${limit}&offset=${offset}`,
        { headers },
      );
      const body = await res.json();
      if (!res.ok) throw new Error(`Brevo [${res.status}]: ${JSON.stringify(body)}`);
      const contacts: any[] = body.contacts ?? [];
      for (const c of contacts) {
        const e = String(c.email ?? "").trim().toLowerCase();
        if (e) emails.add(e);
      }
      if (contacts.length < limit) break;
      offset += limit;
    }

    if (emails.size === 0) return { added: 0, skipped: 0, total: 0 };

    const rows = [...emails].map((email) => ({
      email,
      label: data.label,
      source: "brevo",
    }));
    const { data: inserted, error } = await supabaseAdmin
      .from("marketing_recipients")
      .upsert(rows, { onConflict: "email,label", ignoreDuplicates: true })
      .select("id");
    if (error) throw error;
    const added = inserted?.length ?? 0;
    return { added, skipped: emails.size - added, total: emails.size };
  });

export const importInactiveVendors = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ label: z.string().trim().min(1).max(120).default("leadi") }).parse(input ?? {}),
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: vendors, error } = await supabaseAdmin
      .from("vendors")
      .select("profile_id, profiles:profile_id(email)")
      .eq("status", "inactive");
    if (error) throw error;

    const emails = new Set<string>();
    for (const v of vendors ?? []) {
      const e = String((v as any).profiles?.email ?? "").trim().toLowerCase();
      if (e && /.+@.+\..+/.test(e)) emails.add(e);
    }
    if (emails.size === 0) return { added: 0, skipped: 0, total: 0 };

    const rows = [...emails].map((email) => ({
      email,
      label: data.label,
      source: "inactive-vendors",
    }));
    const { data: inserted, error: insErr } = await supabaseAdmin
      .from("marketing_recipients")
      .upsert(rows, { onConflict: "email,label", ignoreDuplicates: true })
      .select("id");
    if (insErr) throw insErr;
    const added = inserted?.length ?? 0;
    return { added, skipped: emails.size - added, total: emails.size };
  });

export const listImportedRecipients = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { count: total, error: cErr } = await supabaseAdmin
      .from("marketing_recipients")
      .select("*", { count: "exact", head: true });
    if (cErr) throw cErr;

    const byLabel = new Map<string, number>();
    const pageSize = 1000;
    let from = 0;
    while (true) {
      const { data, error } = await supabaseAdmin
        .from("marketing_recipients")
        .select("label")
        .range(from, from + pageSize - 1);
      if (error) throw error;
      const rows = data ?? [];
      for (const r of rows) {
        const k = (r.label ?? "—") as string;
        byLabel.set(k, (byLabel.get(k) ?? 0) + 1);
      }
      if (rows.length < pageSize) break;
      from += pageSize;
    }

    // Ensure known labels are always shown even when empty
    if (!byLabel.has("Prijavljeni na novičke")) byLabel.set("Prijavljeni na novičke", 0);
    if (!byLabel.has("Poslana povpraševanja")) byLabel.set("Poslana povpraševanja", 0);

    const labels = [...byLabel.entries()]
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count);
    return { total: total ?? 0, labels };
  });

export const pasteRecipients = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        label: z.string().trim().min(1).max(120),
        source: z.string().trim().min(1).max(60).default("paste"),
        text: z.string().min(1).max(2_000_000),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    const { assertAdmin } = await import("./admin.server");
    await assertAdmin(context.userId);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const emailRe = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
    const matches = data.text.match(emailRe) ?? [];
    const emails = new Set<string>();
    for (const m of matches) emails.add(m.trim().toLowerCase());

    if (emails.size === 0) return { added: 0, skipped: 0, total: 0 };

    const rows = [...emails].map((email) => ({
      email,
      label: data.label,
      source: data.source,
    }));
    const { data: inserted, error } = await supabaseAdmin
      .from("marketing_recipients")
      .upsert(rows, { onConflict: "email,label", ignoreDuplicates: true })
      .select("id");
    if (error) throw error;
    const added = inserted?.length ?? 0;
    return { added, skipped: emails.size - added, total: emails.size };
  });
